import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import settingsAtom from "../../../../atoms/settings/SettingsAtom";
import LayoutComponent from "../../../../components/dashboard/Mapview/LayoutComponent";
import dashboardAtoms, { fetchBlocks, fetchBulkData, fetchMapSize, fetchpierlegends} from "../../../../atoms/Dashboard/DashboardAtom";
import ToggleButtonComponent from "../../../../shared/Dashboard/ToogleButton";
import MultipleSelectComponent from "../../../../shared/MultiSelectComponent";
import { SectionList } from "../../../../shared/Helper";
import LogsChecklistRightPanel from "./rightPanel";
import { getSelectedTrackerRows, handleSelectionChange } from "@product-fusion/surya-fw/dist/utils/selectionUtils";
import punchListAtom, { fetchDownloadAuditLogsAtom, fetchGenerateNewITRAtom, fetchReportsData } from "../../../../atoms/quality/QualityAtoms";
import GenerateNewITRModal from "./generateNewITRModal";
import { ItcandItrColumns } from "../../../../shared/constent/constent";
import TableComponent from "./TableComponent";

const BlockDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const projectId = localStorage.getItem("project_id");

    const [loader, setLoader] = useState(false);
    const [data] = useAtom(dashboardAtoms);
    const [settings] = useAtom(settingsAtom);

    const [view, setView] = useState("Logs");
    const [selectedRow, setSelectedRow] = useState({
        count: null,
        sections: {},
        trackerTypes: {}
    });
    const [section, setSection] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedTrackerRows, setSelectedTrackerRows] = useState([]);

    const [showGenerateNewITRModal, setShowGenerateNewITRModal] = useState(false)
    const [modalLoader, setModalLoader] = useState(false);
    const [formData, setFormData] = useState({
        visibility: 'internal',
        itcModule: '',
        itrReferenceNumber: '',
    });
    const [, setMapSize] = useAtom(fetchMapSize);
    const [, setBulk] = useAtom(fetchBulkData);
    const [, setBlock] = useAtom(fetchBlocks);
    const [, setPiers] = useAtom(fetchpierlegends);
    const [, setDownloadAuditLogs] = useAtom(fetchDownloadAuditLogsAtom);
    const [, setGenerateITR] = useAtom(fetchGenerateNewITRAtom);
    const updateFormField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const [type, setType] = useState("ITC");
    const [visibility, setVisibility] = useState("external")
    const [initialFilter, setInitialFilter] = useState({})
    const [currentFilter, setCurrentFilter] = useState({
        block: [],
        row: [],
        scope: [],
        section: [],
    });
    const [list, setList] = useState({
        block: [],
        row: [],
        scope: [],
        section: [],
    });

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });

    const [, setTableValue] = useAtom(fetchReportsData)
    const [reportData] = useAtom(punchListAtom)
    const [open, setOpen] = useState(false);
    const [filters, setFilters] = useState([]);
    const { tableData = [] } = reportData;
    const [settingsData] = useAtom(settingsAtom);
    const { blockList } = settingsData;
    const filterableColumns = ["block", "row", "section", "scope"];


    useEffect(() => {
        if (tableData?.filter_values && filters?.length === 0) {
            setInitialFilter(tableData.filter_values);
            setList(tableData.filter_values);
            setCurrentFilter(
                {
                    block: [],
                    row: [],
                    scope: [],
                    section: [],
                }
            )
        } else if (filters?.length) {
            const filterKeys = filters
                .map(f => Object.keys(f)[0])
                .filter((_, index) => index === 0)

            const resetFilterValues = filterKeys.reduce((acc, key) => {
                acc[key] = initialFilter[key];
                return acc;
            }, {});

            setList({
                ...tableData.filter_values,
                ...resetFilterValues,
            });

            setCurrentFilter(tableData.filter_values)

        }
    }, [tableData, filters, initialFilter]);


    const fetchAllData = useCallback(async () => {
        setLoader(true);
        try {
            await Promise.allSettled([
                setBulk(id),
                setMapSize(id),
                setBlock(),
                setPiers("")
            ]);

        } catch (error) {
            console.error("❌ Error in fetchAllData:", error);
        } finally {
            setLoader(false);
        }
    }, [setBulk, setMapSize, setBlock, setPiers, id]);

    useEffect(() => {
        if (projectId && id) {
            fetchAllData();
        }
    }, [projectId, fetchAllData, id]);

    useEffect(() => {
        if (view === "Lock/Unlock") {
            navigate("/quality/lock-and-unlock");
        }
    }, [view, navigate]);

    useEffect(() => {
        if (id && blockList?.[id]) {
            setFilters((prev) => {
                const exists = prev.some((item) => item.block === blockList[id]);
                if (exists) return prev;
                return [...prev, { block: [blockList[id]] }];
            });
        }
    }, [id, blockList]);

    const handleSectionChange = async (event) => {
        const value = event.target.value;
        const data = section.filter((item) => !value.includes(item));

        setSection(typeof value === 'string' ? value.split(',') : value);

        const sectionValue = await handleSelectionChange("section", value, data);
        setSelectedRow(sectionValue);
    };

    const handleClickSelectAll = async () => {
        const sectionValue = await handleSelectionChange("selectAll");
        setSelectedRow(sectionValue);
        setSelectAll(true);
        setSection(SectionList(data?.bulkAllData?.piers));
    };

    const handleClearSelection = async () => {
        await handleSelectionChange("clear");
        setSelectedRow({
            count: null,
            sections: {},
            trackerTypes: {}
        });
        setSelectAll(false);
        setSection([]);
    };

    useEffect(() => {
        let selectedTrackers = getSelectedTrackerRows();
        setSelectedTrackerRows(selectedTrackers);
    }, [selectedRow]);

    // Download functions for audit logs
    const handleDownloadCPLog = async () => {
        try {
            if (selectedTrackerRows.length > 0) {
                await setDownloadAuditLogs({
                    activity_type: "cp",
                    tracker_row_ids: selectedTrackerRows,
                    block_id: id
                });
            } else {
                await setDownloadAuditLogs({
                    activity_type: "cp",
                    block_id: id
                });
            }
        } catch (error) {
            console.error("Error downloading CP log:", error);
        }
    };

    const handleDownloadQCLog = async () => {
        try {
            if (selectedTrackerRows.length > 0) {
                await setDownloadAuditLogs({
                    activity_type: "qc",
                    tracker_row_ids: selectedTrackerRows,
                    block_id: id
                });
            } else {
                await setDownloadAuditLogs({
                    activity_type: "qc",
                    block_id: id
                });
            }
        } catch (error) {
            console.error("Error downloading QC log:", error);
        }
    };

    // ITC/ITR modal handlers
    const handleOpenITCITRModal = async () => {
        setOpen(true);
    };
    const handleOpenGenerateNewITRModal = async () => {
        setShowGenerateNewITRModal(true);
    };

    const handleCloseGenerateNewITRModal = () => {
        setShowGenerateNewITRModal(false);
    };
    const handleGenerateNewITR = async () => {
        setModalLoader(true);
        try {

            await setGenerateITR({
                punchlistType: formData.visibility,
                referenceNumber: formData.itrReferenceNumber,
                module: formData.itcModule
            });
            setFormData({
                visibility: 'internal',
                itcModule: '',
                itrReferenceNumber: '',
            })
            handleCloseGenerateNewITRModal()
        } catch (error) {
            console.error("Error generating new ITR:", error);
        } finally {
            setModalLoader(false);
        }
    };


    const downloadButtons = [
        {
            id: 'cp-log',
            label: 'Download CP log',
            handler: handleDownloadCPLog,
            disabled: false
        },
        {
            id: 'qc-log',
            label: 'Download QC log',
            handler: handleDownloadQCLog,
            disabled: false
        },
        {
            id: 'new-itr',
            label: 'Generate ITR',
            handler: handleOpenGenerateNewITRModal,
            disabled: selectedTrackerRows.length === 0
        },
        {
            id: 'itr',
            label: 'Download ITC and ITR',
            handler: handleOpenITCITRModal,
            disabled: false
        }
    ];


    return (
        <Box sx={{ height: 'calc(100vh - 200px)', width: "100%", p: 0 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", py: 2, px: 2 }}>
                <Typography variant="span" fontSize={22} sx={{ fontWeight: "bold" }}>
                    Logs and Checklists
                </Typography>
                <ToggleButtonComponent
                    data={["Lock/Unlock", "Logs"]}
                    active={view}
                    setActive={setView}
                />
            </Box>

            <Box px={4} sx={{ borderTop: '1px solid #e0e0e0', display: "flex" }}>
                <Box sx={{ width: "70%", borderRight: '1px solid #e0e0e0' }}>
                    {loader ? (
                        <Box sx={{
                            height: "calc(100vh - 200px)",
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", m: -2, pt: 2, pr: 2 }}>
                                <MultipleSelectComponent
                                    data={SectionList(data?.bulkAllData?.piers)}
                                    section={section}
                                    setSection={handleSectionChange}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleClickSelectAll}
                                    sx={{
                                        margin: 1,
                                        minWidth: '120px',
                                        backgroundColor: !selectAll ? '#f16253' : '#ccc',
                                        '&:hover': {
                                            backgroundColor: !selectAll ? '#d9493a' : '#ccc'
                                        }
                                    }}
                                >
                                    Select All
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={selectedTrackerRows.length === 0}
                                    onClick={handleClearSelection}
                                    sx={{
                                        margin: 1,
                                        minWidth: '120px',
                                        backgroundColor: selectedTrackerRows.length > 0 ? '#f16253' : '#ccc',
                                        '&:hover': {
                                            backgroundColor: selectedTrackerRows.length > 0 ? '#d9493a' : '#ccc'
                                        }
                                    }}
                                >
                                    Clear Selection
                                </Button>
                            </Box>

                            <LayoutComponent
                                loader={loader}
                                data={data}
                                qcProgressData={{}}
                                projectList={settings?.projectList}
                                setSelectedRow={setSelectedRow}
                                selectedRow={selectedRow}
                                userSelection={{
                                    showType: "pier",
                                    selection: "piers"
                                }}
                                currentSelection="piers"
                                layoutType="QC"
                                showLegend={false}
                            />
                        </>
                    )}
                </Box>
                <Box sx={{ width: '30%' }}>
                    <LogsChecklistRightPanel
                        downloadButtons={downloadButtons}
                    />
                </Box>
            </Box>

            <GenerateNewITRModal
                open={showGenerateNewITRModal}
                onClose={handleCloseGenerateNewITRModal}
                formData={formData}
                updateFormField={updateFormField}
                loading={modalLoader}
                onProceed={handleGenerateNewITR}
            />

            <TableComponent
                open={open}
                onClose={() => setOpen(!open)}
                type={type}
                setType={setType}
                visibility={visibility}
                setVisibility={setVisibility}
                data={reportData?.tableData?.data}
                columns={ItcandItrColumns}
                list={list}
                setList={setList}
                currentFilterValue={currentFilter}
                setCurrentFilterValue={setCurrentFilter}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                filters={filters}
                setFilters={setFilters}
                initialFilter={initialFilter}
                setTableValue={setTableValue}
                filterableColumns={filterableColumns}
            />
        </Box>
    );
};

export default BlockDetail;